import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { EmptySearchResults } from '@provider-portal/common/components/EmptySearchResults/EmptySearchResults';
import { SearchInput } from '@provider-portal/common/components/SearchInput';
import { $tid } from '@provider-portal/common/utils/testIdConfig';

import { NoImageIcon } from '../../icons/NoImage';
import { ModalDialogActions } from '../ModalDialogActions/ModalDialogActions';
import { ModalDialogTitle } from '../ModalDialogTitle/ModalDialogTitle';
import { GrandChild } from './types';

const styles = {
  searchInput: {
    backgroundColor: 'colors.neutral200',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'colors.neutral200',
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'colors.neutral200',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'colors.neutral200',
    },
  },
  imgSize: {
    '& img': {
      width: '53px',
      height: '40px',
    },
  },
  disableColor: {
    color: 'colors.neutral400',
    '& img': {
      width: '53px',
      height: '40px',
      filter: 'opacity(0.4)',
    },
  },
  dialogTitle: { borderBottom: 'solid thin', borderColor: 'colors.neutral400' },
  dialogActions: { borderTop: 'solid thin', borderColor: 'colors.neutral400', pt: 2 },
};

export interface ListItemElementPriceDetails {
  price: number;
  currency: string;
  price_str: string;
}

export interface ListItemElement {
  id: string;
  label: string;
  imageUrl?: string;
  children?: GrandChild[];
  priceDetails?: ListItemElementPriceDetails;
  isDisabled?: boolean;
  disableReason?: string;
}

export interface SelectItemsDialogFCProps {
  open: boolean;
  isBulkTagsEditMode?: boolean;
  strings: Record<
    | 'cardTitle'
    | 'searchLabel'
    | 'noSearchResultsTitle'
    | 'noSearchResultsSubtitle'
    | 'cancel'
    | 'addItems',
    string
  >;
  itemList: ListItemElement[];
  selectedItemList: string[];
  indeterminateTags?: string[];
  onClickCancel: () => void;
  onClickAddItems: (selectedItems: string[], indeterminate?: string[]) => void;
  shouldShowImages: boolean;
}

interface ListItemsProps {
  itemList: ListItemElement[];
  shouldShowImages: boolean;
  indeterminate: string[];
  isBulkTagsEditMode?: boolean;
  currentSelectedItems: string[];
  onItemChecked: (id: string, isChecked: boolean) => void;
}

interface SelectionListItemProps {
  id: string;
  label: string;
  imageUrl?: string;
  checked?: boolean;
  isDisabled: boolean;
  disableReason?: string;
  shouldShowImages: boolean;
  indeterminateTags: string[];
  isBulkTagsEditMode?: boolean;
  onCheck: (id: string, isChecked: boolean) => void;
  priceDetails?: ListItemElementPriceDetails;
}

const SelectionListItem = React.forwardRef<typeof ListItem, SelectionListItemProps>(
  (
    {
      id,
      label,
      imageUrl,
      checked,
      isDisabled,
      disableReason,
      shouldShowImages,
      onCheck,
      indeterminateTags,
      isBulkTagsEditMode,
      priceDetails,
    },
    _ref,
  ) => {
    const [isChecked, setIsChecked] = useState<boolean>(!!checked);
    useEffect(() => {
      setIsChecked(!!checked); // Update state when checked prop changes
    }, [checked]);

    const checkUncheckHandler = useCallback(() => {
      onCheck(id, !isChecked);
      setIsChecked(!isChecked);
    }, [id, isChecked, onCheck]);

    return (
      <ListItem
        sx={isDisabled ? styles.disableColor : { color: 'inherit' }}
        data-testid={`${$tid.components.SelectItemsDialog.itemName}__${label}`}
      >
        <ListItemButton onClick={isDisabled ? undefined : checkUncheckHandler}>
          {isDisabled ? (
            <Tooltip title={disableReason} arrow placement="right">
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': `checkbox-list-label-${id}` }}
                  checked={isChecked}
                  disabled={isDisabled}
                  indeterminate={isBulkTagsEditMode && indeterminateTags.includes(id)}
                />
              </ListItemIcon>
            </Tooltip>
          ) : (
            <ListItemIcon>
              <Checkbox
                edge="start"
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-labelledby': `checkbox-list-label-${id}` }}
                checked={isChecked}
                disabled={isDisabled}
                indeterminate={isBulkTagsEditMode && indeterminateTags.includes(id)}
              />
            </ListItemIcon>
          )}

          {shouldShowImages ? (
            <ListItemIcon sx={isDisabled ? styles.disableColor : styles.imgSize}>
              {imageUrl ? <img srcSet={imageUrl} alt="" /> : <NoImageIcon />}
              <Box mr={1} />
            </ListItemIcon>
          ) : null}
          <ListItemText id={`checkbox-list-label-${id}`} primary={label} />
          {priceDetails ? (
            <ListItemIcon sx={isDisabled ? styles.disableColor : { color: 'inherit' }}>
              <ListItemText
                id={`checkbox-list-currency-${id}`}
                primary={`${priceDetails.price_str}`}
              />
            </ListItemIcon>
          ) : null}
        </ListItemButton>
      </ListItem>
    );
  },
);

const ListItems: React.FC<ListItemsProps> = memo(
  ({
    itemList,
    shouldShowImages,
    currentSelectedItems,
    onItemChecked,
    isBulkTagsEditMode,
    indeterminate,
  }) => {
    const onCheckHandler = useCallback(
      (itemId, isChecked) => {
        onItemChecked(itemId, isChecked);
      },
      [onItemChecked],
    );
    const generateItemList = useMemo(() => {
      return itemList.map((value) => {
        let isChecked = currentSelectedItems.indexOf(value.id) > -1;

        if (isBulkTagsEditMode) {
          isChecked =
            currentSelectedItems.indexOf(value.id) > -1 && !indeterminate.includes(value.id);
        }

        return (
          <SelectionListItem
            id={value.id}
            label={value.label}
            imageUrl={value.imageUrl}
            key={value.id}
            isDisabled={value.isDisabled ?? false}
            disableReason={value.disableReason}
            priceDetails={value.priceDetails}
            isBulkTagsEditMode={isBulkTagsEditMode}
            indeterminateTags={indeterminate}
            shouldShowImages={shouldShowImages}
            checked={isChecked}
            onCheck={onCheckHandler}
          />
        );
      });
    }, [
      currentSelectedItems,
      itemList,
      onCheckHandler,
      shouldShowImages,
      isBulkTagsEditMode,
      indeterminate,
    ]);
    return (
      <Box>
        <List>{generateItemList}</List>
      </Box>
    );
  },
);

export const SelectItemsDialogFC = memo(function SelectItemsDialogFC({
  open,
  strings,
  itemList,
  selectedItemList,
  indeterminateTags,
  onClickCancel,
  onClickAddItems,
  shouldShowImages,
  isBulkTagsEditMode = false,
}: SelectItemsDialogFCProps) {
  const [searchedString, setSearchedString] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>(selectedItemList);
  const [indeterminate, setIndeterminate] = useState<string[]>(indeterminateTags ?? []);

  const onSearchStringChange = useCallback((searchStr) => {
    setSearchedString(searchStr);
  }, []);

  const onClickAddItemsHandler = useCallback(() => {
    if (isBulkTagsEditMode) {
      onClickAddItems(selectedItems, indeterminate);
    } else {
      onClickAddItems(selectedItems);
    }
  }, [onClickAddItems, selectedItems, isBulkTagsEditMode, indeterminate]);

  const onItemCheckedHandler = useCallback(
    (id: string, isChecked: boolean) => {
      if (isBulkTagsEditMode && indeterminate.includes(id)) {
        setIndeterminate(indeterminate.filter((i) => i !== id));
      }
      setSelectedItems((prevArray) => {
        const currentSelectedItems = new Set(prevArray);
        if (isChecked) {
          currentSelectedItems.add(id);
        } else {
          currentSelectedItems.delete(id);
        }
        return Array.from(currentSelectedItems);
      });
    },
    [indeterminate, isBulkTagsEditMode],
  );

  const displayItems = useMemo(() => {
    if (!searchedString.length) {
      return itemList ?? [];
    }

    return itemList.filter((item) => {
      const searchRegexp = new RegExp(
        `${searchedString.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')}`,
        'i',
      );
      return item.label.match(searchRegexp);
    });
  }, [searchedString, itemList]);

  const showItemList = useMemo(() => {
    if (!displayItems.length) {
      return <EmptySearchResults strings={strings} />;
    }
    return (
      <ListItems
        itemList={displayItems}
        isBulkTagsEditMode={isBulkTagsEditMode}
        indeterminate={indeterminate}
        onItemChecked={onItemCheckedHandler}
        currentSelectedItems={selectedItems}
        shouldShowImages={shouldShowImages}
      />
    );
  }, [
    displayItems,
    onItemCheckedHandler,
    selectedItems,
    shouldShowImages,
    strings,
    isBulkTagsEditMode,
    indeterminate,
  ]);

  useEffect(() => {
    setSelectedItems(selectedItemList);
  }, [selectedItemList]);

  useEffect(() => {
    setSearchedString('');
  }, [open]);

  return (
    <Box>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ sx: { maxWidth: 'initial', minWidth: { md: '600px' } } }}
      >
        <DialogTitle id="alert-dialog-title" sx={styles.dialogTitle}>
          <ModalDialogTitle cardTitle={strings.cardTitle} onClickClose={onClickCancel} />
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <SearchInput searchLabel={strings.searchLabel} onChange={onSearchStringChange} />
          </Box>
          <Box>{showItemList}</Box>
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <ModalDialogActions
            actionLabel={
              isBulkTagsEditMode
                ? strings.addItems
                : `${strings.addItems}${selectedItems.length ? ` - ${selectedItems.length}` : ''}`
            }
            cancelLabel={strings.cancel}
            onClickCancel={onClickCancel}
            onClickAction={onClickAddItemsHandler}
            actionDisabled={!isBulkTagsEditMode && !selectedItems.length}
            testIds={{
              save: $tid.components.SelectItemsDialog.buttons.add,
              cancel: $tid.components.SelectItemsDialog.buttons.cancel,
            }}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
});
